import { location } from 'cdui-js';
import type { AgreementSceneDocItem } from '../../../api/agreement-doc';
import { fetchUserInfo } from '../../../api/login';
import { submitTradeProductActivation, type TradeProductActivationSubmitReq } from '../../../api/user';
import { goBackByBrowser } from '../../../lib/browser';
import { loadAgreementDocsByCodesWithPreload } from '../../../lib/agreement-pdf-cache';
import { leaveToApp } from '../deposit/h5-deposit/appBridge';
import { applyUserInfo, userState } from '../../../stores/user';
import { commonState, loadSysConfigurations } from '../../../stores/common';

export type TradeProductKey = 'derivatives' | 'chinaConnect';

/** TODO(trade-product-protocol): 正式 scene/doc 到位后替换 */
const SCENE_LOGIN_REGISTER = 'SCENE_LOGIN_REGISTER';
const SCENE_DERIVATIVE = 'SCENE_OPEN_ACCOUNT_DERIVATIVE_PRODUCT_TRADE';
const DOC_DISCLAIMER = 'AGR001';
const DOC_DERIVATIVES_RISK = 'AGR003';

const isTruthyFlag = (v: unknown) =>
  v === true || v === 1 || v === '1' || String(v).toLowerCase() === 'true';

const tradePermissionOf = (userInfo: any): Record<string, unknown> => {
  const root = userInfo?.data ?? userInfo ?? {};
  const raw = root?.tradePermission;
  return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
};

export const isDerivativesGranted = (userInfo?: any) =>
  isTruthyFlag(tradePermissionOf(userInfo ?? userState.userInfo).derivatives);

export const isChinaConnectGranted = (userInfo?: any) =>
  isTruthyFlag(tradePermissionOf(userInfo ?? userState.userInfo).chinaConnect);

/**
 * A 股沪深是否都在禁用列表（字典 MARKET_TRADE_DISABLED）；口径与 App `isChinaAMarketFullyClosed` 一致。
 * 产品约定生产环境沪深同开同关；只配一个仅见于开发自测。
 */
export const isChinaAMarketFullyClosed = (): boolean => {
  const set = new Set(commonState.marketTradeDisabled);
  if (set.size === 0) return false;
  if (set.has('CN') || set.has('A')) return true;
  const shClosed = set.has('MA') || set.has('SH') || set.has('SS');
  const szClosed = set.has('MZ') || set.has('SZ');
  return shClosed && szClosed;
};

/** 系统配置未就绪时拉一次（App WebView 通常已由 Theme 灌入） */
export const ensureSysConfigurationsReady = async () => {
  if (commonState.sysConfigReady) return;
  await loadSysConfigurations().catch(() => null);
};

export const parsePreselect = (raw: unknown): TradeProductKey | null => {
  const v = String(raw ?? '').trim();
  if (v === 'derivatives' || v === 'chinaConnect') return v;
  return null;
};

export const readPreselectFromUrl = (): TradeProductKey | null => {
  try {
    const fromLoc = (location.query as Record<string, string> | undefined)?.preselect;
    if (fromLoc) return parsePreselect(fromLoc);
  } catch {
    /* ignore */
  }
  if (typeof window === 'undefined') return null;
  return parsePreselect(new URLSearchParams(window.location.search).get('preselect'));
};

export const resolveInitialSelected = (
  derivativesGranted: boolean,
  chinaConnectGranted: boolean,
  preselect?: TradeProductKey | null,
  chinaConnectAvailable = true
): TradeProductKey | null => {
  const chinaConnectSelectable = chinaConnectAvailable && !chinaConnectGranted;
  if (preselect === 'derivatives' && !derivativesGranted) return 'derivatives';
  if (preselect === 'chinaConnect' && chinaConnectSelectable) return 'chinaConnect';
  if (!derivativesGranted) return 'derivatives';
  if (chinaConnectSelectable) return 'chinaConnect';
  return null;
};

export const buildSubmitBody = (selected: TradeProductKey): TradeProductActivationSubmitReq =>
  selected === 'derivatives' ? { derivatives: true } : { chinaConnect: true };

export const activateTradeProduct = (selected: TradeProductKey) =>
  submitTradeProductActivation(buildSubmitBody(selected));

export const refreshUserTradePermission = async () => {
  const info = await fetchUserInfo().catch(() => null);
  if (info) applyUserInfo(info);
  return info;
};

export type TradeProductDocs = {
  chinaConnectDoc: AgreementSceneDocItem | null;
  derivativesRiskDoc: AgreementSceneDocItem | null;
};

/** TODO(trade-product-protocol): 正式协议替换 */
export const loadTradeProductDocs = async (): Promise<TradeProductDocs> => {
  const [china, deriv] = await Promise.all([
    loadAgreementDocsByCodesWithPreload({
      sceneCode: SCENE_LOGIN_REGISTER,
      docCodes: [DOC_DISCLAIMER],
    }).catch(() => null),
    loadAgreementDocsByCodesWithPreload({
      sceneCode: SCENE_DERIVATIVE,
      docCodes: [DOC_DERIVATIVES_RISK],
    }).catch(() => null),
  ]);
  return {
    chinaConnectDoc: china?.docMap?.[DOC_DISCLAIMER] ?? null,
    derivativesRiskDoc: deriv?.docMap?.[DOC_DERIVATIVES_RISK] ?? null,
  };
};

const inAppWebView = () => typeof window !== 'undefined' && !!(window as any).ReactNativeWebView;

/** 返回：App WebView 出栈；独立 H5 浏览器后退 */
export const leaveTradeProducts = () => {
  if (inAppWebView()) {
    leaveToApp();
    return;
  }
  goBackByBrowser();
};

export const successUrl = (product: TradeProductKey) => {
  const path = location.path.startsWith('/h5/') ? '/h5/trade-products/success' : '/trade-products/success';
  return `${path}?product=${encodeURIComponent(product)}`;
};

export const mainUrl = () => (location.path.startsWith('/h5/') ? '/h5/trade-products' : '/trade-products');
